import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
  PLACE_FOUND_OPTIONS,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAuth } from '@/hooks'
import { schema } from './validationSchema'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { FormComponentProps, UserGroups } from '@/types'
import {
  PHYSICAL_INSPECTION_SPECIE_OPTIONS,
  OTHER_SPECIE_OPTION_VALUE,
  PHYSICAL_INSPECTION_BAT_SPECIE_VALUE,
  PHYSICAL_INSPECTION_BOX_TYPE_OPTIONS,
  PHYSICAL_INSPECTION_SPECIE_SPEC_OPTIONS,
  IS_TRAIL_FOUND_OPTIONS,
  WIND_DIRECTION_OPTIONS,
  HEIGHT_OPTIONS,
} from '../fieldsOptions'

export const PhysicalInspectionForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      other_specie: '',
      specie_spec: '',
      specie_spec_other: '',
      box_type: '',
      box_number: '',
      is_place_found: '',
      is_trail_found: '',
      orientation: '',
      height: '',
      notes: '',
      observed_at: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="specie"
              label={t('observation-log.fieldLabels.specie')}
              options={PHYSICAL_INSPECTION_SPECIE_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch('specie') === OTHER_SPECIE_OPTION_VALUE && (
            <Box width="25%">
              <ControlledTextField
                control={methods.control}
                name="other_specie"
                placeholder={t('observation-log.fieldLabels.otherSpecie')}
                errorMessagePrefix=""
              />
            </Box>
          )}
          {methods.watch('specie') !== PHYSICAL_INSPECTION_BAT_SPECIE_VALUE && (
            <>
              <Box width="25%">
                <ControlledSelect
                  control={methods.control}
                  name="specie_spec"
                  placeholder={t('observation-log.fieldLabels.specieSpec')}
                  options={PHYSICAL_INSPECTION_SPECIE_SPEC_OPTIONS}
                  disabled={disabledForTheUser}
                  errorMessagePrefix=""
                />
              </Box>
              {methods.watch('specie_spec') === OTHER_SPECIE_OPTION_VALUE && (
                <Box width="25%">
                  <ControlledTextField
                    control={methods.control}
                    name="specie_spec_other"
                    placeholder={t(
                      'observation-log.fieldLabels.specieSpecOther'
                    )}
                    disabled={disabledForTheUser}
                    errorMessagePrefix=""
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="box_type"
            label={t('observation-log.fieldLabels.boxType')}
            options={PHYSICAL_INSPECTION_BOX_TYPE_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <Box width="25%">
          <ControlledTextField
            control={methods.control}
            name="box_number"
            placeholder={t('observation-log.fieldLabels.boxNumber')}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="is_place_found"
            label={t('observation-log.fieldLabels.isPlaceFound')}
            options={PLACE_FOUND_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="is_trail_found"
            label={t('observation-log.fieldLabels.isTrailFound')}
            options={IS_TRAIL_FOUND_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="orientation"
            label={t('observation-log.fieldLabels.orientation')}
            options={WIND_DIRECTION_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="height"
            label={t('observation-log.fieldLabels.height')}
            options={HEIGHT_OPTIONS}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <ControlledTextField
          control={methods.control}
          name="notes"
          placeholder={t('observation-log.fieldLabels.notes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
      </Stack>

      <ObservationLogFormActions
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        isLoading={isLoading}
        methods={methods}
      />
    </FormProvider>
  )
}
